import React from 'react';

function CookiePolicy() {
  const handleBack = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Back Button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span className="mr-2">←</span>
              Back
            </button>
          </div>

          <h1 className="text-4xl font-bold text-white mb-8 text-center">
            Cookie Policy
          </h1>
          
          <div className="text-gray-300 space-y-6">
            <p className="text-sm text-gray-400 mb-8">
              <strong>Last Updated:</strong> January 8, 2025
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. What Are Cookies?</h2>
              <p className="mb-4">
                Cookies are small text files stored on your device when you visit websites. They help websites 
                remember information about your visit, such as your preferred settings and login status.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Cookies</h2>
              
              <div className="space-y-4">
                <div className="bg-blue-900 border border-blue-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-100 mb-2">🔐 Essential Cookies</h3>
                  <p className="text-blue-100 mb-2">These cookies are necessary for the website to function properly:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-100 text-sm">
                    <li><strong>Authentication:</strong> Keep you logged in securely</li>
                    <li><strong>Security:</strong> Protect against cross-site request forgery</li>
                    <li><strong>Session Management:</strong> Maintain your trading session</li>
                    <li><strong>Load Balancing:</strong> Ensure optimal server performance</li>
                  </ul>
                </div>

                <div className="bg-green-900 border border-green-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-100 mb-2">⚙️ Functional Cookies</h3>
                  <p className="text-green-100 mb-2">These cookies enhance your experience:</p>
                  <ul className="list-disc list-inside space-y-1 text-green-100 text-sm">
                    <li><strong>Preferences:</strong> Remember your dashboard settings</li>
                    <li><strong>Language:</strong> Store your language preferences</li>
                    <li><strong>Theme:</strong> Remember dark/light mode choice</li>
                    <li><strong>Trading Settings:</strong> Save your risk preferences</li>
                  </ul>
                </div>

                <div className="bg-purple-900 border border-purple-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-100 mb-2">📊 Analytics Cookies</h3>
                  <p className="text-purple-100 mb-2">These cookies help us improve our service:</p>
                  <ul className="list-disc list-inside space-y-1 text-purple-100 text-sm">
                    <li><strong>Usage Statistics:</strong> Understand how you use the platform</li>
                    <li><strong>Performance Monitoring:</strong> Track loading times and errors</li>
                    <li><strong>Feature Usage:</strong> See which features are most popular</li>
                    <li><strong>Error Tracking:</strong> Identify and fix technical issues</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Third-Party Cookies</h2>
              
              <div className="bg-gray-700 p-4 rounded-lg">
                <p className="mb-3">We use carefully selected third-party services that may set their own cookies:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Stripe:</strong> Payment processing and subscription management</li>
                  <li><strong>Hyperliquid:</strong> Exchange integration and grid trading functionality</li>
                  <li><strong>SendGrid:</strong> Email delivery and communication</li>
                  <li><strong>Market Data Providers:</strong> Real-time financial data</li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">
                  These services have their own cookie policies. We encourage you to review them.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Cookie Duration</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-yellow-800 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-yellow-100 mb-2">⏰ Session Cookies</h3>
                  <p className="text-yellow-100 text-sm">
                    Temporary cookies that expire when you close your browser. Used for essential functions.
                  </p>
                </div>
                
                <div className="bg-orange-800 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-orange-100 mb-2">📅 Persistent Cookies</h3>
                  <p className="text-orange-100 text-sm">
                    Remain on your device for a set period. Used for preferences and analytics (30 days max).
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Managing Your Cookies</h2>
              
              <div className="bg-blue-900 border border-blue-700 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-100 mb-4">Browser Controls</h3>
                <p className="text-blue-100 mb-3">You can control cookies through your browser settings:</p>
                <ul className="list-disc list-inside space-y-2 text-blue-100">
                  <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                  <li><strong>Firefox:</strong> Preferences → Privacy & Security → Cookies and Site Data</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                  <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
                </ul>
              </div>

              <div className="bg-red-900 border border-red-700 p-4 rounded-lg mt-4">
                <h3 className="text-lg font-semibold text-red-100 mb-2">⚠️ Important Note</h3>
                <p className="text-red-100">
                  Disabling essential cookies may prevent actuvo.ai from functioning properly. You may not be able to log in or access your trading dashboard.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Your Consent</h2>
              
              <p className="mb-4">
                By continuing to use actuvo.ai, you consent to our use of cookies as described in this policy.
              </p>
              
              <div className="bg-green-900 border border-green-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-100 mb-2">✅ What You're Agreeing To</h3>
                <ul className="list-disc list-inside space-y-1 text-green-100">
                  <li>Essential cookies for website functionality</li>
                  <li>Functional cookies to improve your experience</li>
                  <li>Analytics cookies to help us improve the service</li>
                  <li>Third-party cookies from our trusted partners</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Data Protection</h2>
              
              <p className="mb-4">
                Cookies contain limited information and cannot access files on your computer or install malware. 
                We protect cookie data with the same security measures as other personal information.
              </p>
              
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Encryption:</strong> Sensitive cookie data is encrypted</li>
                <li><strong>Secure Transmission:</strong> Cookies are transmitted over HTTPS</li>
                <li><strong>Limited Access:</strong> Only authorized systems can access cookie data</li>
                <li><strong>Regular Cleanup:</strong> Expired cookies are automatically removed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Updates to This Policy</h2>
              
              <p className="mb-4">
                We may update this Cookie Policy periodically to reflect changes in our practices or applicable laws. 
                Updates will be posted on this page with a revised date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Contact Us</h2>
              
              <div className="bg-gray-700 p-4 rounded-lg">
                <p className="mb-2">If you have questions about our cookie practices:</p>
                <p><strong>Email:</strong> privacy@actuvo.ai</p>
                <p><strong>Subject:</strong> Cookie Policy Question</p>
                <p><strong>Website:</strong> https://actuvo.ai</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-blue-900 border border-blue-700 rounded-lg">
              <p className="text-blue-100">
                <strong>Remember:</strong> You can manage your cookie preferences at any time through your browser settings. 
                For the best experience with actuvo.ai, we recommend allowing essential and functional cookies.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;